import React, { FormEventHandler, useCallback } from 'react'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import PhoneInput from 'components/Common/Form/Input/PhoneInput'
import Group from 'components/utils/Group'
import TextArea from 'components/Common/Form/Input/TextArea'
import { formToObject } from 'lib/forms/formToObject'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import ModalBase from 'components/Luxkit/Modal/ModalBase'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import ModalFooter from 'components/Luxkit/Modal/ModalFooter'
import { connect } from 'react-redux'
import TextInput from 'components/Common/Form/Input/TextInput'
import HiddenInput from 'components/Common/Form/Input/HiddenInput'
import LineCalendarIcon from 'components/Luxkit/Icons/line/LineCalendarIcon'
import AgentCallbackRequestDateTimeInput from '../RequestAgentCallbackDateTimeInput'
import { parseSelectedNumberOfTravellersToFormText, parseSelectedTravelDatesToFormText } from '../RequestAgentCallbackUtils'

export interface RequestAgentCallbackResult {
  firstName: string;
  lastName: string;
  type: App.CallbackType;
  email: string;
  phone: { phone: string, prefix: string };
  destination: string;
  travelDates: string;
  message: string;
  numberOfTravellers: string;
  callbackDate: Date;
  callbackTime: string;
  timezone: string;
}

interface MappedStateProps {
  account: App.AuthAccount;
  filters?: App.OfferListFilters;
}

interface OwnProps {
  type: App.CallbackType;
  filters?: App.OfferListFilters;
  location?: string;
}

function RequestAgentCallbackModal(props: MappedStateProps & OwnProps) {
  const { account, type, filters, location } = props

  const hotelsAndVillasCallback: App.CallbackType = 'HOTELS_AND_VILLAS'
  const lesaCallback: App.CallbackType = 'LESA'

  const maxLength = 100
  const modalElement = useModalElementContext<RequestAgentCallbackResult | undefined>()

  const handleSubmit = useCallback<FormEventHandler<HTMLFormElement>>(async(e) => {
    e.preventDefault()

    const requestAgentCallbackFormData = formToObject<RequestAgentCallbackResult>(e.currentTarget)
    modalElement.resolve(requestAgentCallbackFormData)
  }, [modalElement])

  return (
    <ModalBase>
      <form onSubmit={handleSubmit}>
        <ModalHeader title="Request a callback"/>
        <ModalBody>
          <ModalContent>
            <VerticalSpacer gap={20}>
              <BodyTextBlock variant="large">
                {type === hotelsAndVillasCallback && `Hi ${account.givenName}, you are entitled to a personalised concierge call to assist with your booking.
              Share your contact details, and our 24/7 concierge team will get back to you as soon as possible.`}
                {type === lesaCallback && ' Share your details with us, and our support team will get back to you as soon as possible.'}
              </BodyTextBlock>
              <Group direction="vertical" gap={8}>
                {type === lesaCallback &&
                  <Group direction="horizontal" gap={8} horizontalAlign="stretch">
                    <TextInput
                    name="firstName"
                    defaultValue={account.givenName}
                    invalidErrorMessage="Invalid characters. Please use only letters and spaces"
                    label="First name *"
                    maxLength={maxLength}
                    minLength={2}
                    pattern="^[A-Za-z ]+$"
                    placeholder="Your first name"
                    required
                    requiredErrorMessage="First name is required"
                  />
                    <TextInput
                    name="lastName"
                    defaultValue={account.surname}
                    invalidErrorMessage="Invalid characters. Please use only letters and spaces"
                    label="Last name *"
                    maxLength={maxLength}
                    minLength={2}
                    pattern="^[A-Za-z ]+$"
                    placeholder="Your last name"
                    required
                    requiredErrorMessage="Last name is required"
                  />
                  </Group>
                }
                <AgentCallbackRequestDateTimeInput />
                <PhoneInput
                  defaultPhoneNumber={account.phone}
                  defaultPhonePrefix={account.phonePrefix}
                  label="Mobile number"
                  name="phone"
                  placeholder="e.g. 412 345 678"
                  required
                  requiredErrorMessage="Mobile number is required"
                />
                {type === hotelsAndVillasCallback &&
                  <TextInput
                    name="destination"
                    defaultValue={location}
                    invalidErrorMessage="Invalid characters. Please use only letters and spaces"
                    label="Where do you plan to go?"
                    maxLength={maxLength}
                    minLength={2}
                    autoComplete="off"
                    pattern="^[A-Za-z ]+$"
                    placeholder="The destination you would like to travel to"
                    required
                    requiredErrorMessage="Destination is required"
                  />
                }
                {type === hotelsAndVillasCallback && filters &&
                  <TextInput
                    name="travelDates"
                    defaultValue={parseSelectedTravelDatesToFormText(filters)}
                    label="When are you looking to travel?"
                    maxLength={maxLength}
                    minLength={2}
                    autoComplete="off"
                    placeholder="The dates you would like to travel on"
                    required
                    startIcon={<LineCalendarIcon />}
                    requiredErrorMessage="Dates are required"
                  />
                }
                {type === hotelsAndVillasCallback && filters &&
                  <TextInput
                    name="numberOfTravellers"
                    defaultValue={parseSelectedNumberOfTravellersToFormText(filters)}
                    label="How many people will you be travelling with?"
                    maxLength={maxLength}
                    minLength={2}
                    autoComplete="off"
                    placeholder="The number of people you will be travelling with"
                    required
                    requiredErrorMessage="Number of people you are travelling with is required"
                  />
                }
                {type === lesaCallback &&
                  <TextInput
                    invalidErrorMessage="Invalid email address"
                    defaultValue={account.email}
                    label="Email address *"
                    maxLength={maxLength}
                    name="email"
                    placeholder="Enter your business email"
                    required
                    requiredErrorMessage="Email address is required"
                    type="email"
                  />
                }
                {type === lesaCallback &&
                  <TextArea
                    label="Message"
                    maxLength={maxLength}
                    name="message"
                    placeholder="e.g. I have a payment issue"
                    type="message"
                  />
                }
                {type === hotelsAndVillasCallback && <TextArea
                  label="Any other questions or comments?"
                  maxLength={maxLength}
                  name="message"
                  placeholder="e.g. Do I need a vaccine?"
                  type="message"
                />
                }
                <HiddenInput
                  name="type"
                  value={type}
                />
              </Group>
            </VerticalSpacer>
          </ModalContent>
        </ModalBody>
        <ModalFooter
        primaryActionProps={{ children: 'Submit Request', type: 'submit' }}
      />
      </form>
    </ModalBase>
  )
}

export default connect<MappedStateProps, undefined, Partial<OwnProps>, App.State>(
  (state, ownProps) => ({
    account: state.auth.account,
    filters: ownProps.filters,
    type: ownProps.type,
  }),
)(RequestAgentCallbackModal)
